import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { type Currency } from "@/lib/currency";
import { Badge } from "@/components/ui/badge";
import { 
  TrendingUp, 
  TrendingDown, 
  Wallet, 
  Users, 
  Clock, 
  CheckCircle,
  BarChart3,
  Calendar
} from "lucide-react";
import { formatCurrency } from "@/lib/currency";

interface Participant {
  id: string;
  email: string;
  has_paid: boolean;
}

interface Request {
  id: string;
  product_name: string;
  total_price: number;
  status: string;
  created_at: string;
  deadline: string;
  participants: Participant[];
}

interface PerformanceAnalyticsProps {
  requests: Request[];
  currency: Currency;
}

export function PerformanceAnalytics({ requests, currency }: PerformanceAnalyticsProps) {
  const analytics = useMemo(() => {
    const now = new Date();
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);

    // This month stats
    const thisMonthRequests = requests.filter(r => new Date(r.created_at) >= thisMonth);
    const lastMonthRequests = requests.filter(r => {
      const d = new Date(r.created_at);
      return d >= lastMonth && d <= lastMonthEnd;
    });

    const thisMonthRevenue = thisMonthRequests
      .filter(r => r.status === 'completed')
      .reduce((sum, r) => sum + r.total_price, 0);
    const lastMonthRevenue = lastMonthRequests
      .filter(r => r.status === 'completed')
      .reduce((sum, r) => sum + r.total_price, 0);

    const revenueChange = lastMonthRevenue > 0 
      ? ((thisMonthRevenue - lastMonthRevenue) / lastMonthRevenue * 100)
      : thisMonthRevenue > 0 ? 100 : 0;

    // Collection success rate
    const completedCount = requests.filter(r => r.status === 'completed').length;
    const totalCount = requests.filter(r => r.status !== 'pending').length;
    const successRate = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

    // Average collection size
    const avgSize = completedCount > 0
      ? requests.filter(r => r.status === 'completed').reduce((s, r) => s + r.total_price, 0) / completedCount
      : 0;

    // Payment speed - avg days from creation to completion
    const completedWithDates = requests.filter(r => r.status === 'completed');
    const avgDays = completedWithDates.length > 0
      ? completedWithDates.reduce((sum, r) => {
          const created = new Date(r.created_at);
          const deadline = new Date(r.deadline);
          return sum + Math.max(1, Math.ceil((deadline.getTime() - created.getTime()) / (1000 * 60 * 60 * 24)));
        }, 0) / completedWithDates.length
      : 0;

    // Monthly breakdown (last 6 months)
    const monthlyData: { month: string; revenue: number; collections: number }[] = [];
    for (let i = 5; i >= 0; i--) {
      const monthStart = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthEnd = new Date(now.getFullYear(), now.getMonth() - i + 1, 0);
      const monthName = monthStart.toLocaleDateString('en-US', { month: 'short' });
      
      const monthRequests = requests.filter(r => {
        const d = new Date(r.created_at);
        return d >= monthStart && d <= monthEnd;
      });
      
      monthlyData.push({
        month: monthName,
        revenue: monthRequests.filter(r => r.status === 'completed').reduce((s, r) => s + r.total_price, 0),
        collections: monthRequests.length,
      });
    }

    // Top clients by total paid amount
    const clientTotals: Record<string, number> = {};
    requests.filter(r => r.status === 'completed').forEach(r => {
      r.participants.filter(p => p.has_paid).forEach(p => {
        const email = p.email.toLowerCase();
        clientTotals[email] = (clientTotals[email] || 0) + (r.total_price / r.participants.length);
      });
    });
    const topClients = Object.entries(clientTotals)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([email, total]) => ({ email, total }));

    return {
      thisMonthRevenue,
      revenueChange,
      successRate,
      avgSize,
      avgDays: Math.round(avgDays),
      monthlyData,
      topClients,
      totalCollections: requests.length,
      activeCollections: requests.filter(r => r.status === 'pending').length,
    };
  }, [requests]);

  const maxRevenue = Math.max(...analytics.monthlyData.map(m => m.revenue), 1);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold">Performance</h2>
        <p className="text-sm text-muted-foreground">Track your collection metrics and revenue</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">This Month</p>
              {analytics.revenueChange !== 0 && (
                <Badge variant="outline" className={analytics.revenueChange > 0 ? "text-emerald-600 border-emerald-300" : "text-red-600 border-red-300"}>
                  {analytics.revenueChange > 0 ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
                  {Math.abs(Math.round(analytics.revenueChange))}%
                </Badge>
              )}
            </div>
            <p className="text-2xl font-bold">{formatCurrency(analytics.thisMonthRevenue, currency)}</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-muted-foreground mb-2">Success Rate</p>
            <p className="text-2xl font-bold">{analytics.successRate}%</p>
            <p className="text-xs text-muted-foreground">{analytics.totalCollections} total collections</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-muted-foreground mb-2">Avg Collection</p>
            <p className="text-2xl font-bold">{formatCurrency(analytics.avgSize, currency)}</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-muted-foreground mb-2">Avg Duration</p>
            <p className="text-2xl font-bold">{analytics.avgDays} days</p>
            <p className="text-xs text-muted-foreground">creation to deadline</p>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Chart (simple bar chart) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-cyan-600" />
            Monthly Revenue
          </CardTitle>
          <CardDescription>Last 6 months</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-3 h-40">
            {analytics.monthlyData.map((m) => (
              <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-xs text-muted-foreground font-medium">
                  {m.revenue > 0 ? formatCurrency(m.revenue, currency) : '–'}
                </span>
                <div 
                  className="w-full bg-gradient-to-t from-cyan-500 to-teal-400 rounded-t-md transition-all min-h-[4px]"
                  style={{ height: `${Math.max((m.revenue / maxRevenue) * 100, 3)}%` }}
                />
                <span className="text-xs text-muted-foreground">{m.month}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Top Clients */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-4 w-4 text-cyan-600" />
            Top Paying Clients
          </CardTitle>
        </CardHeader>
        <CardContent>
          {analytics.topClients.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No completed collections yet</p>
          ) : (
            <div className="space-y-3">
              {analytics.topClients.map((client, i) => (
                <div key={client.email} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-muted-foreground w-5">{i + 1}.</span>
                    <span className="text-sm truncate max-w-[200px]">{client.email}</span>
                  </div>
                  <span className="text-sm font-semibold">{formatCurrency(client.total, currency)}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
